/**
 * Copy for the assets configuration screen (ADR 0003 / epic ALC-1655).
 *
 * A separate namespace from `assets`, which covers the registry, the review queue and leases.
 * Kept apart because the register this screen writes in is different: it is explaining
 * accounting consequence to whoever is about to change a policy, not labelling a table.
 *
 * `errors.*` mirrors every stable `ASSET_CONFIG_*` code the API can return. A code with no
 * translation is a 400 the client cannot act on, so a new code on the server means a new key
 * here in the same change.
 */
export const assetsConfigurationPtBr = {
  page: {
    tabs: {
      rules: 'Regras de capitalização',
      policy: 'Política da empresa',
      bookRules: 'Vida útil por livro',
      classes: 'Classes de bens',
    },
  },

  filters: {
    entity: 'Empresa',
    framework: 'Norma contábil',
    allEntities: 'Todas as empresas',
    allFrameworks: 'Todas as normas',
    clear: 'Limpar filtros',
    // The blank entries are the tabs where the filter *does* apply: the tooltip only renders
    // on a disabled control, so those strings are never read. Kept so the two maps stay the
    // same shape as FILTER_SCOPE — a tab that later loses a filter needs a reason to show.
    entityNotApplicable: {
      rules: '',
      policy: '',
      bookRules: 'A régua pertence ao livro, e o livro é que carrega a empresa. Filtre pela norma.',
      classes: 'A classe é da conta inteira, não de uma empresa.',
    },
    frameworkNotApplicable: {
      rules: '',
      policy: 'A política é da empresa. A norma vem dos livros que a empresa tem.',
      bookRules: '',
      classes: '',
    },
  },

  loadError: {
    title: 'Não foi possível carregar a configuração',
    description:
      'O servidor não respondeu. Esta tela não sabe o que está cadastrado agora, então não afirma ' +
      'nada sobre a sua configuração. Tente de novo.',
    retry: 'Tentar de novo',
  },

  rules: {
    title: 'Regras de capitalização',
    create: {
      title: 'Nova regra de capitalização',
      description:
        'Identifique compras que devem passar pela revisão de imobilizado. A regra exige confirmação humana e não define o direito ao crédito fiscal.',
      name: 'Nome da regra',
      entity: 'Entidade',
      assetClass: 'Classe do bem',
      selectClass: 'Selecione a classe',
      ncm: 'Prefixo NCM',
      keyword: 'Trecho da descrição',
      matchers:
        'Informe ao menos um critério. Quando ambos forem preenchidos, o item deverá atender aos dois.',
      acknowledge:
        'Entendo que uma regra baseada somente na descrição pode sugerir itens incorretos e revisarei cada candidato.',
      required:
        'Preencha nome, entidade, classe e ao menos um critério válido. Use somente dígitos no NCM e confirme a revisão quando usar apenas a descrição.',
      saved:
        'Regra criada. Os próximos itens classificados que atenderem aos critérios serão enviados à revisão de imobilizado.',
      save: 'Criar regra',
      cancel: 'Cancelar',
      close: 'Fechar',
    },
    empty: {
      title: 'Nenhuma regra cadastrada',
      description:
        'Sem nenhuma regra ativa, nenhuma nota gera candidato: a fila de revisão fica vazia e ' +
        'todas as compras são lançadas como despesa, sem aviso.',
      action: 'Criar a primeira regra',
    },
    queueOff: {
      title: 'A fila de revisão está desligada',
      description:
        'Não há nenhuma regra ativa neste tenant. Enquanto isso durar, nenhuma compra é ' +
        'proposta como imobilizado.',
    },
    columns: {
      priority: 'Prioridade',
      name: 'Regra',
      matchers: 'Critérios',
      target: 'Classe de destino',
      autoConfirm: 'Confirmação',
      origin: 'Origem',
      status: 'Situação',
    },
    autoConfirm: {
      on: 'Confirma sozinha',
      off: 'Exige revisão',
      explain:
        'As linhas que esta regra casar são capitalizadas e contabilizadas sem revisão ' +
        'humana. Uma capitalização errada é um erro contábil, não um incômodo de interface.',
      explainOff: 'Um humano confirma cada linha que esta regra propuser.',
    },
    origin: {
      curated: 'Curada',
      learned: 'Aprendida',
      curatedExplain: 'Escrita por uma pessoa ou entregue na configuração inicial.',
      learnedExplain:
        'O sistema criou esta regra a partir de uma única decisão tomada na fila de revisão. ' +
        'Tem um caso por trás, não um padrão — vale conferir se ela continua acertando.',
    },
    status: {
      ACTIVE: 'Ativa',
      INACTIVE: 'Inativa',
      DRAFT: 'Rascunho',
      DELETED: 'Removida',
    },
    staleAccountVersion: 'Conta fora da versão',
    staleAccountVersionExplain:
      'A conta desta regra não está na versão publicada do plano de contas. O classificador ' +
      'tenta resolvê-la pelo de-para e descarta a regra se não conseguir — ela pode estar sem ' +
      'decidir nada.',
    matcher: {
      account: 'Conta',
      vendor: 'Fornecedor',
      ncm: 'NCM',
      cfop: 'CFOP',
      keyword: 'Palavra-chave',
      minUnitAmount: 'Valor unitário mínimo',
      none: 'Sem critérios',
      andJoiner: 'e',
      hint:
        'Todos os critérios informados precisam casar ao mesmo tempo. O NCM casa por início ' +
        '("8471" pega toda a linha de computadores); o CFOP casa exato, com 4 dígitos.',
    },
    actions: {
      create: 'Nova regra',
      edit: 'Editar',
      activate: 'Ativar',
      deactivate: 'Desativar',
      save: 'Salvar',
      cancel: 'Cancelar',
    },
    form: {
      name: 'Nome da regra',
      namePlaceholder: 'Ex.: Notebooks e desktops (NCM 8471)',
      priority: 'Prioridade',
      priorityHint: 'Menor é avaliada primeiro. A primeira regra que casar vence.',
      classId: 'Classe de destino',
      entityId: 'Empresa',
      entityAll: 'Todas as empresas',
      minUnitAmountHint:
        'Comparado com o valor UNITÁRIO, nunca com o total da linha — senão cinquenta cabos ' +
        'de R$ 20 comprados juntos viravam um bem.',
      acknowledgeKeywordOnly: 'Confirmo que esta palavra-chave distingue as compras',
      acknowledgeKeywordOnlyHint:
        'Uma regra que casa só por palavra-chave dispara em qualquer descrição que contenha o ' +
        'texto. Prefira NCM, CFOP, conta ou fornecedor sempre que souber qual usar.',
      allowAutoConfirm: 'Permitir confirmação automática',
      allowAutoConfirmHint:
        'Deixa a regra capitalizar e contabilizar sem revisão humana quando nada ficar ' +
        'ambíguo. Comece desligado e só ligue depois de ver a regra acertar na fila.',
    },
    toast: {
      created: 'Regra criada.',
      updated: 'Regra atualizada.',
      activated: 'Regra ativada.',
      deactivated: 'Regra desativada.',
    },
  },

  policy: {
    title: 'Política da empresa',
    missingDefault: {
      title: 'Nenhuma política cadastrada',
      description:
        'Sem política não há limite contra o qual julgar: nenhuma compra é proposta como ' +
        'imobilizado, qualquer que seja o valor.',
    },
    scope: {
      label: 'Escopo',
      tenantDefault: 'Padrão do tenant',
      entity: 'Empresa específica',
      hint: 'A política da empresa prevalece sobre o padrão do tenant.',
    },
    fields: {
      capitalizationThresholdAmount: 'Limite de capitalização',
      capitalizationThresholdAmountHint:
        'Aplicado POR UNIDADE, não pelo total da linha. Uma classe pode ter limite próprio.',
      defaultIncrementalBorrowingRate: 'Taxa incremental de empréstimo',
      defaultIncrementalBorrowingRateHint:
        'Ao ano. Não existe taxa oficial: é o custo de captação da própria empresa, e só ela ' +
        'sabe qual é. Pode ser sobrescrita por contrato.',
      lowValueLeaseThresholdAmount: 'Limite de arrendamento de baixo valor',
      lowValueLeaseThresholdAmountHint:
        'Por ativo subjacente. O CPC 06 não fixa valor — este número é política da empresa e ' +
        'precisa estar documentado.',
      shortTermLeaseMaxMonths: 'Prazo máximo de curto prazo',
      shortTermLeaseMaxMonthsHint:
        'Contratos com prazo até este limite usam a isenção de curto prazo. O CPC 06 (R2) ' +
        '§5(a) fixa o teto em 12 meses — não é possível definir um prazo maior.',
      shortTermLeaseMaxMonthsCeilingError:
        'O CPC 06 (R2) §5(a) e o Apêndice A fixam a isenção de curto prazo em até 12 meses na ' +
        'data de início do contrato. Esse teto é da norma, não da empresa, e não pode ser ' +
        'ultrapassado — um prazo menor (por exemplo, 6 meses) é permitido.',
      capLeaseholdImprovementToTerm: 'Limitar benfeitoria ao prazo do contrato',
      capLeaseholdImprovementToTermHint:
        'Amortiza a benfeitoria no prazo restante do arrendamento em vez da vida útil econômica.',
    },
    actions: { save: 'Salvar política', edit: 'Editar' },
    toast: { saved: 'Política salva.' },
    createOverride: {
      description:
        'Nenhuma política específica para {{entity}}. Até que uma seja criada, as compras ' +
        'desta empresa seguem o padrão do tenant mostrado acima.',
      action: 'Criar política para esta empresa',
    },
    createDefault: {
      action: 'Criar padrão do tenant',
    },
    edit: {
      title: 'Editar política',
      titleNewOverride: 'Criar política para {{entity}}',
      titleNewDefault: 'Criar padrão do tenant',
      newOverrideHint:
        'Os valores abaixo começam iguais aos do padrão do tenant. Ajuste o que for diferente ' +
        'para esta empresa e salve para criar o registro específico dela — o padrão do tenant ' +
        'não é alterado.',
      newDefaultHint:
        'Nenhum padrão do tenant existe ainda. Sem ele, nenhuma compra é julgada contra um ' +
        'limite e nada é proposto como imobilizado até que este formulário seja salvo.',
      cancel: 'Cancelar',
      toast: { saved: 'Política salva.' },
    },
  },

  bookRules: {
    edit: {
      action: 'Editar',
      title: 'Editar régua',
      // Level 1, stated so the reader knows the omission is a decision, not an oversight.
      frameworkLocked: 'A norma vem do livro e não é editável.',
      // The fiscal lock (ALC-2978 048). Shown above the fields instead of the framework line
      // above so a reader does not need to infer "TAX_BASIS" implies "read-only" from the chip.
      fiscalLocked:
        'Este livro mede sob IN RFB 1.700/2017 Anexo III. A vida útil, o método e o residual ' +
        'não são uma estimativa da empresa e não podem ser alterados aqui — só o Fundamento é ' +
        'mostrado, para conferência.',
      fiscalLifeHint: 'Definida pelo Anexo III da IN RFB 1.700/2017, não editável nesta tela.',
      lifeHint: 'Em meses. É a estimativa da empresa e o CPC 27 §51 pede revisão pelo menos anual.',
      lifeIgnoredWhenNone:
        'Com "Não deprecia" o número não é usado, mas o campo é obrigatório. Deixe 1.',
      residualHint: 'Quanto do custo sobra ao fim da vida útil. Precisa ser menor que 100%.',
      rateHint: 'Informativa. Quem deprecia é a vida útil em meses.',
      normPlaceholder: 'CPC 27, laudo de engenharia 03/2026, IN RFB 1.700/2017 Anexo III',
      normHint: 'Obrigatório: é o que sustenta a estimativa numa auditoria.',
      cancel: 'Cancelar',
      save: 'Salvar régua',
      toast: { saved: 'Régua salva. Vale para os próximos bens capitalizados.' },
    },
    title: 'Vida útil por livro',
    notRetroactive:
      'Alterar aqui vale para os próximos bens capitalizados. Bens já em serviço congelaram a ' +
      'régua na capitalização e continuam pela anterior.',
    empty: {
      title: 'Nenhuma régua cadastrada',
      description:
        'Uma classe sem régua em nenhum livro não capitaliza: o registro do bem é recusado.',
    },
    columns: {
      class: 'Classe',
      book: 'Livro',
      framework: 'Norma',
      method: 'Método',
      usefulLife: 'Vida útil',
      residual: 'Residual',
      referenceRate: 'Taxa de referência',
      norm: 'Fundamento',
    },
    method: {
      STRAIGHT_LINE: 'Linear',
      DECLINING_BALANCE: 'Saldo decrescente',
      UNITS_OF_PRODUCTION: 'Unidades produzidas',
      NONE: 'Não deprecia',
    },
    months_one: '{{count}} mês',
    months_other: '{{count}} meses',
    fields: {
      usefulLifeMonths: 'Vida útil (meses)',
      residualRate: 'Valor residual (%)',
      referenceAnnualRate: 'Taxa anual de referência (%)',
      referenceAnnualRateHint:
        'Informativa: o cálculo usa a vida útil em meses. Fica registrada para o relatório ' +
        'citar a taxa que o contador espera ver.',
      normReference: 'Fundamento normativo',
      normReferencePlaceholder: 'Ex.: IN RFB 1.700/2017 Anexo III',
    },
    actions: { edit: 'Editar régua', save: 'Salvar' },
    toast: { saved: 'Régua salva.' },
  },

  classes: {
    empty: {
      title: 'Nenhuma classe visível',
      description:
        'Nenhuma classe corresponde à norma dos livros desta empresa. Fale com a Alchemy se ' +
        'esperava ver classes aqui.',
    },
    title: 'Classes de bens',
    columns: {
      code: 'Código',
      name: 'Nome',
      group: 'Grupo',
      assetKind: 'Natureza fiscal',
      threshold: 'Limite próprio',
      unitization: 'Unitização',
      bookRules: 'Réguas',
      assets: 'Bens',
      status: 'Situação',
    },
    locked: 'Definido pela Alchemy',
    lockedHint:
      'Código, grupo, natureza fiscal e direito de uso não são editáveis: eles resolvem a ' +
      'depreciação admitida pela legislação e a apresentação no balanço. Para mudar a natureza ' +
      'de um bem, mova-o para outra classe.',
    rouRegraHint:
      'Classes de direito de uso não têm régua cadastrada aqui de propósito: o CPC 06 (R2) §32 ' +
      'amortiza pelo menor entre o prazo do contrato e a vida útil, e o prazo é do contrato, não ' +
      'da classe. O reconhecimento do arrendamento calcula essa régua sozinho.',
    noBookRules: 'Sem régua — nada capitaliza nesta classe',
    usefulLife: {
      action: 'Definir vida útil',
      description:
        'A CPC 27 §51 põe a vida útil na estimativa da empresa. A régua fiscal ao lado vem da ' +
        'IN RFB 1.700/2017 Anexo III e é somente leitura — só o livro societário é gravado aqui.',
      unavailable:
        'Esta classe não tem um par de réguas — uma societária e uma fiscal — para comparar. ' +
        'Use a aba “Vida útil por livro”, que nomeia o livro em cada linha.',
    },
    bookRuleFromContract: 'Régua definida pelo contrato',
    bookRuleFromContractExplain:
      'Um direito de uso amortiza pelo menor entre o prazo do contrato e a vida útil do bem ' +
      'subjacente (CPC 06 (R2) §32). O prazo vem de cada contrato, não desta classe — o ' +
      'reconhecimento do arrendamento já calcula a régua sozinho, mesmo sem nenhuma linha aqui. ' +
      'Não é configuração faltando.',
    noBookRulesLeaseholdExplain:
      'Uma benfeitoria também amortiza pelo prazo do contrato, mas o motor que calcularia essa ' +
      'régua na capitalização ainda não existe. Capitalizar nesta classe hoje é recusado com ' +
      'aviso claro, não silenciosamente — não é um esquecimento de configuração.',
    thresholdInherited: 'Herda o limite da política',
    group: {
      PROPERTY_PLANT_EQUIPMENT: 'Imobilizado',
      RIGHT_OF_USE: 'Direito de uso',
      INTANGIBLE: 'Intangível',
    },
    unitization: {
      PER_UNIT: 'Um bem por unidade',
      AGGREGATE: 'Um bem por linha',
      ASK: 'Perguntar',
    },
    rightOfUse: 'Direito de uso',
    leaseholdImprovement: 'Benfeitoria',
    fields: {
      name: 'Nome',
      description: 'Observação',
      capitalizationThresholdAmount: 'Limite próprio da classe',
      capitalizationThresholdAmountHint: 'Vazio herda o limite da política da empresa.',
      unitizationPolicy: 'Unitização',
      isLeaseholdImprovement: 'É benfeitoria em imóvel de terceiros',
    },
    create: {
      action: 'Nova classe',
      title: 'Criar classe de ativo',
      description:
        'Defina a classe de apresentação da empresa. A régua societária será decidida depois; ' +
        'a fiscal continuará protegida pela legislação.',
      headerDescription: 'Inclua as classes que representam a operação e as notas explicativas.',
      code: 'Código',
      codePlaceholder: 'EQUIPAMENTOS_MEDICOS',
      framework: 'Framework contábil',
      select: 'Selecione',
      fiscalLock:
        'A natureza fiscal não é escolhida aqui. A nova classe nasce sem um padrão fiscal até ' +
        'que a classificação respaldada pela legislação seja definida pela Alchemy.',
      cancel: 'Cancelar',
      submit: 'Criar classe',
      saving: 'Criando...',
      discardTitle: 'Descartar a nova classe?',
      discardDescription: 'Os dados preenchidos serão perdidos se você fechar este formulário.',
      discard: 'Descartar',
      keepEditing: 'Continuar preenchendo',
      frameworkLoadError:
        'Não foi possível carregar os frameworks contábeis. Tente novamente antes de criar a classe.',
      retryFrameworks: 'Tentar novamente',
    },
    actions: { edit: 'Editar', save: 'Salvar' },
    toast: { saved: 'Classe salva.', created: 'Classe criada.' },
  },

  onboarding: {
    repair: {
      title: 'Padrões fiscais incompletos',
      description:
        'O livro fiscal existe, mas faltam classes ou regras padrão necessárias para revisar os ativos.',
      action: 'Restaurar padrões fiscais',
      confirmTitle: 'Restaurar padrões fiscais?',
      confirmDescription:
        'Serão criados somente padrões ausentes para esta entidade. Decisões e regras já configuradas serão preservadas.',
      confirm: 'Restaurar padrões',
      success: 'Padrões fiscais restaurados. Confira as classes e regras antes de continuar.',
      error: 'Não foi possível restaurar os padrões fiscais.',
    },
    loading: 'Carregando a configuração do módulo de ativos…',
    unsaved: {
      title: 'Descartar a estimativa ainda não salva?',
      description:
        'A escolha de vida útil desta classe será perdida. Salve a estimativa antes de continuar ou permaneça nesta etapa.',
      discard: 'Descartar e continuar',
      stay: 'Permanecer nesta etapa',
    },
    entity: 'Empresa',
    selectEntity: 'Selecione a empresa',
    steps: {
      books: 'Livros',
      accounts: 'Mapeamento de contas',
      classRules: 'Réguas das classes',
      policy: 'Política da empresa',
    },
    actions: {
      leave: 'Ir para ativos',
      previous: 'Voltar',
      continue: 'Continuar: {{step}}',
      finish: 'Concluir e ir para ativos',
    },
    loadError: { title: 'Não foi possível verificar a configuração' },
    books: {
      title: 'Prepare as duas réguas de mensuração',
      description:
        'O livro societário registra o CPC; o fiscal mede em paralelo sem lançar no razão.',
      statutory: 'Régua societária',
      fiscal: 'Régua fiscal',
      statutoryMissing: 'Não configurada',
      fiscalMissing: 'Pendente',
      createStatutoryFirst:
        'Esta empresa ainda não possui um livro societário ativo. Crie o livro nas configurações ' +
        'contábeis antes de adicionar a régua fiscal.',
      createStatutory: 'Criar livro societário',
      createTitle: 'Criar a régua fiscal desta empresa',
      code: 'Código do livro',
      name: 'Nome do livro',
      memoExplanation:
        'O guia criará o livro com framework TAX_BASIS e modo MEMO, além do escopo de mensuração. ' +
        'Ele nunca contabiliza lançamentos; suas vidas úteis vêm da IN RFB 1.700/2017 Anexo III.',
      create: 'Criar livro fiscal',
      creating: 'Criando...',
      created: 'Livro fiscal e réguas legais criados.',
    },
    accounts: {
      title: 'Mapeie as nove contas obrigatórias',
      description:
        'Sem estes nove slots FA, capitalização, depreciação, baixa e arrendamentos não conseguem contabilizar.',
      complete: 'Todos os slots obrigatórios estão mapeados.',
      pending: 'Há slots obrigatórios sem conta no plano ativo desta empresa.',
      configure: 'Configurar contas do sistema',
    },
    classRules: {
      title: 'Decida a régua societária das classes',
      description:
        'CPC 27 §51 exige que a empresa estime a vida útil. O valor sem resposta continua ' +
        'alinhado ao fiscal, mas não conta como uma decisão da entidade.',
      fiscalLockedTitle: 'A régua fiscal é somente leitura',
      fiscalLocked:
        'Vida útil, método e residual do livro TAX_BASIS vêm da IN RFB 1.700/2017 Anexo III. ' +
        'Nem este guia nem a tela de regras podem alterá-los.',
      unavailable:
        'Esta classe ainda não tem uma régua societária cadastrada — configure-a na aba "Vida ' +
        'útil por livro" antes de responder aqui.',
      question: 'A empresa deprecia {{className}} pelo ciclo real de troca ou pela taxa fiscal?',
      realCycle: 'Pelo ciclo real de troca',
      realCycleDescription:
        'Informe a estimativa aprovada pela empresa. Nenhuma vida útil é sugerida pela Alchemy.',
      fiscalRate: 'Pela taxa fiscal',
      fiscalRateDescription:
        'A empresa decide que sua estimativa econômica coincide com a vida fiscal exibida ao lado.',
      months: 'Vida útil societária em meses',
      noDefault: 'Sem valor padrão',
      statutorySide: 'Societária — estimativa da empresa',
      fiscalSide: 'Fiscal — somente leitura',
      noFiscalRule:
        'Sem régua fiscal ainda. Esta classe não tem natureza fiscal, e o livro fiscal ativo ' +
        'não tem uma régua para ela — salvar a decisão societária cria uma cópia espelhada aqui.',
      referenceAmount: 'Valor de referência do bem para calcular a diferença temporária',
      additionTitle: 'Esta decisão gera uma adição temporária',
      additionDescription:
        'A depreciação societária excedente será adicionada na Parte A do e-Lalur e revertida ' +
        'ao longo da vida fiscal. Informe um valor acima para ver a diferença mensal e do primeiro ano.',
      exclusionTitle: 'Esta decisão gera uma exclusão temporária',
      exclusionDescription:
        'A depreciação fiscal excedente será excluída na Parte A do e-Lalur e revertida ' +
        'ao longo da vida societária. Informe um valor acima para ver a diferença mensal e do primeiro ano.',
      answered: 'Respondida',
      pending: 'Resposta pendente',
      save: 'Salvar decisão societária',
      saving: 'Salvando...',
      saved: 'Régua societária atualizada. A fiscal permaneceu intacta.',
      // Step 1 (2026-09-04): shown instead of the line above when saving this rule mirrored a
      // fiscal one — the fiscal side was NOT left intact, and the toast must not claim it was.
      savedWithMirror:
        'Régua societária atualizada. Uma régua fiscal foi criada como espelho — confirme a ' +
        'vida útil fiscal com o contador.',
      saveError:
        'Não foi possível salvar a decisão societária. Revise a vida útil informada e tente novamente.',
    },
    policy: {
      title: 'Revise a política da empresa',
      description:
        'Confirme o piso de capitalização, o limite de baixo valor, o prazo curto e a taxa incremental.',
      configured: 'Política cadastrada',
      pending: 'Política pendente',
      review: 'Revisar política',
    },
  },

  warnings: {
    title: 'Atenção',
    ASSET_CONFIG_LAST_ACTIVE_RULE_DEACTIVATED:
      'Esta era a última regra ativa. Enquanto nenhuma estiver ativa, nenhuma nota gera ' +
      'candidato: a fila de revisão para de receber itens e as compras são lançadas como ' +
      'despesa sem aviso.',
    ASSET_CONFIG_KEYWORD_ONLY_RULE:
      'Regra baseada apenas em palavra-chave. Dispara em qualquer descrição que contenha o ' +
      'texto — troque por NCM ou CFOP assim que souber quais aparecem nas notas.',
    ASSET_CONFIG_AUTO_CONFIRM_ENABLED:
      'Confirmação automática ligada: as linhas que esta regra casar serão capitalizadas e ' +
      'contabilizadas sem revisão humana.',
    ASSET_CONFIG_BOOK_RULE_NOT_RETROACTIVE:
      'A nova régua vale só para os próximos bens. {{assetsAlreadyInService}} bens já em ' +
      'serviço mantêm a régua congelada na capitalização — mudar a vida útil de um bem em ' +
      'serviço é mudança de estimativa (CPC 23) e se aplica prospectivamente.',
    ASSET_CONFIG_THRESHOLD_NOT_RETROACTIVE:
      'O novo limite vale para as próximas notas classificadas. Candidatos já gerados mantêm o ' +
      'limite registrado no momento da proposta.',
    ASSET_CONFIG_RULE_ACCOUNT_SUPERSEDED_COA:
      'A conta desta regra está numa versão substituída do plano de contas. A regra só casa se ' +
      'houver um de-para não ambíguo até a versão em vigor — sem ele, ela é descartada em ' +
      'silêncio.',
    ASSET_CONFIG_FISCAL_RULE_MIRRORED:
      'A régua fiscal desta classe foi criada como espelho da régua societária que você acabou ' +
      'de salvar — esta classe não tem natureza fiscal e a IN RFB 1.700/2017 Anexo III não tem ' +
      'uma linha para ela. Confirme a vida útil fiscal com o contador e ajuste na aba "Vida ' +
      'útil por livro" se ela precisar ser diferente da societária.',
  },

  errors: {
    ASSET_CONFIG_FIELD_IMMUTABLE:
      'Este campo é definido pela Alchemy e não pode ser alterado: ele resolve a classificação ' +
      'fiscal e a apresentação no balanço. Para mudar a natureza de um bem, mova-o de classe.',
    ASSET_CONFIG_RULE_NO_MATCHER:
      'Uma regra sem nenhum critério casa com todas as linhas de todas as notas. Informe pelo ' +
      'menos conta, fornecedor, NCM, CFOP ou palavra-chave.',
    ASSET_CONFIG_RULE_AMOUNT_ONLY_MATCHER:
      'Só o valor mínimo não identifica o que foi comprado: a regra capturaria honorários, ' +
      'aluguel e guias de imposto acima do valor. Combine o valor com outro critério.',
    ASSET_CONFIG_RULE_KEYWORD_ONLY_UNACKNOWLEDGED:
      'Esta regra casa apenas por palavra-chave, o critério menos confiável. Marque a ' +
      'confirmação no formulário se a palavra realmente distingue estas compras.',
    ASSET_CONFIG_RULE_KEYWORD_ONLY_AUTO_CONFIRM:
      'Uma regra que casa apenas por palavra-chave não pode confirmar automaticamente.',
    ASSET_CONFIG_RULE_CFOP_INVALID: 'O CFOP tem exatamente 4 dígitos.',
    ASSET_CONFIG_RULE_NCM_INVALID: 'O prefixo de NCM aceita apenas dígitos.',
    ASSET_CONFIG_CLASS_NOT_FOUND: 'Classe não encontrada ou inativa.',
    ASSET_CONFIG_CLASS_CODE_DUPLICATE:
      'Já existe uma classe com esse código. Use outro identificador.',
    ASSET_CONFIG_CLASS_CODE_INVALID:
      'O código deve começar por uma letra e usar apenas letras maiúsculas, números e _.',
    ASSET_CONFIG_CLASS_GROUP_ROU_MISMATCH:
      'O grupo e a indicação de direito de uso não podem divergir. Selecione o grupo Direito de uso.',
    ASSET_CONFIG_CLASS_FISCAL_FRAMEWORK_LOCKED:
      'Crie classes na régua societária. O framework TAX_BASIS segue a legislação fiscal e não aceita classes definidas pelo usuário.',
    ASSET_CONFIG_FRAMEWORK_NOT_FOUND:
      'O framework contábil selecionado não existe ou está inativo. Escolha um framework disponível.',
    ASSET_CONFIG_BOOK_CODE_DUPLICATE:
      'Já existe um livro com esse código. Informe outro código para o livro fiscal.',
    ASSET_ONBOARDING_STATUTORY_BOOK_REQUIRED:
      'Crie e vincule o livro societário BR_CPC desta empresa antes de adicionar a régua ' +
      'fiscal. O livro fiscal herda dele o calendário, a moeda e as réguas fiscais. Um ' +
      'livro IFRS não serve de base.',
    ASSET_ONBOARDING_FISCAL_BOOK_EXISTS:
      'Esta empresa já possui um livro fiscal ativo. Atualize o diagnóstico do onboarding.',
    ASSET_ONBOARDING_FISCAL_BOOK_NOT_MEMO:
      'O livro fiscal não pôde ser criado com segurança como MEMO. Nenhuma alteração foi salva.',
    ASSET_CONFIG_RULE_ROU_CLASS_NOT_TARGETABLE:
      'Classes de direito de uso não recebem regra de capitalização: um ativo de direito de ' +
      'uso nasce do reconhecimento de um contrato, nunca de uma linha de nota.',
    ASSET_CONFIG_RULE_ACCOUNT_NOT_MATCHABLE:
      'A conta desta regra pertence a uma versão do plano de contas ainda em rascunho: nenhuma ' +
      'nota foi classificada contra ela, então a regra nunca casaria. Aponte para a conta na ' +
      'versão publicada.',
    ASSET_CONFIG_RULE_NOT_FOUND: 'Regra não encontrada.',
    ASSET_CONFIG_BOOK_NOT_FOUND: 'Livro não encontrado.',
    ASSET_CONFIG_BOOK_RULE_NOT_FOUND: 'Régua não encontrada.',
    ASSET_CONFIG_BOOK_RULE_FISCAL_LOCKED:
      'A vida útil, o método e o residual deste livro seguem a IN RFB 1.700/2017 Anexo III e ' +
      'não são uma estimativa da empresa — não podem ser alterados aqui.',
    ASSET_CONFIG_METHOD_NOT_IMPLEMENTED:
      'Este método ainda não é calculado pelo motor de depreciação. Use Linear (STRAIGHT_LINE) ' +
      'ou Não deprecia (NONE) — os únicos que geram parcelas hoje.',
    ASSET_CONFIG_POLICY_NOT_FOUND: 'Política não encontrada.',
    ASSET_CONFIG_ENTITY_NOT_FOUND: 'Empresa não encontrada.',
    ASSET_CONFIG_USEFUL_LIFE_INVALID: 'A vida útil precisa ser um número inteiro de meses.',
    ASSET_CONFIG_RESIDUAL_RATE_INVALID:
      'O valor residual é uma fração do custo e precisa ser menor que 100%.',
    ASSET_CONFIG_RATE_OUT_OF_RANGE: 'Taxa fora da faixa aceita.',
    ASSET_CONFIG_AMOUNT_INVALID: 'O valor não pode ser negativo.',
    ASSET_CONFIG_SHORT_TERM_MONTHS_INVALID:
      'O prazo de curto prazo precisa ser um número positivo de meses.',
    ASSET_CONFIG_DELETE_NOT_SUPPORTED:
      'Registros desta tela não são excluídos, apenas desativados: o histórico depende deles.',
  },
};

export const assetsConfigurationEn: typeof assetsConfigurationPtBr = {
  page: {
    tabs: {
      rules: 'Capitalisation rules',
      policy: 'Company policy',
      bookRules: 'Useful life per book',
      classes: 'Asset classes',
    },
  },

  filters: {
    entity: 'Entity',
    framework: 'Accounting framework',
    allEntities: 'All entities',
    allFrameworks: 'All frameworks',
    clear: 'Clear filters',
    // The blank entries are the tabs where the filter *does* apply: the tooltip only renders
    // on a disabled control, so those strings are never read. Kept so the two maps stay the
    // same shape as FILTER_SCOPE — a tab that later loses a filter needs a reason to show.
    entityNotApplicable: {
      rules: '',
      policy: '',
      bookRules:
        'A régua belongs to the book, and the book carries the entity. Filter by framework.',
      classes: 'A class is tenant-wide, not owned by one entity.',
    },
    frameworkNotApplicable: {
      rules: '',
      policy: 'A policy belongs to an entity. Its framework comes from the books that entity has.',
      bookRules: '',
      classes: '',
    },
  },

  loadError: {
    title: 'Could not load the configuration',
    description:
      'The server did not answer. This screen does not know what is configured right now, so it ' +
      'makes no claim about your configuration. Try again.',
    retry: 'Try again',
  },

  rules: {
    title: 'Capitalisation rules',
    create: {
      title: 'New capitalisation rule',
      description:
        'Identify purchases requiring fixed-asset review. This rule requires human confirmation and does not establish tax-credit eligibility.',
      name: 'Rule name',
      entity: 'Entity',
      assetClass: 'Asset class',
      selectClass: 'Select a class',
      ncm: 'NCM prefix',
      keyword: 'Description contains',
      matchers: 'Enter at least one criterion. When both are entered, the item must meet both.',
      acknowledge:
        'I understand that matching only by description may suggest incorrect items and I will review each candidate.',
      required:
        'Enter a name, entity, class and at least one valid criterion. Use digits only for NCM and acknowledge review when matching only by description.',
      saved:
        'Rule created. Future classified items matching the criteria will be sent to fixed-asset review.',
      save: 'Create rule',
      cancel: 'Cancel',
      close: 'Close',
    },
    empty: {
      title: 'No rules configured',
      description:
        'With no active rule, no bill raises a candidate: the review queue stays empty and ' +
        'every purchase is booked as an expense, silently.',
      action: 'Create the first rule',
    },
    queueOff: {
      title: 'The review queue is switched off',
      description:
        'This tenant has no active rule. Until one exists, no purchase is proposed as an asset.',
    },
    columns: {
      priority: 'Priority',
      name: 'Rule',
      matchers: 'Matchers',
      target: 'Target class',
      autoConfirm: 'Confirmation',
      origin: 'Origin',
      status: 'Status',
    },
    autoConfirm: {
      on: 'Auto-confirms',
      off: 'Needs review',
      explain:
        'Lines this rule matches are capitalised and posted with no human review. A wrong ' +
        'capitalisation is an accounting error, not a UX annoyance.',
      explainOff: 'A human confirms every line this rule proposes.',
    },
    origin: {
      curated: 'Curated',
      learned: 'Learned',
      curatedExplain: 'Written by a person, or shipped with the initial configuration.',
      learnedExplain:
        'The system wrote this rule from a single decision made in the review queue. It has ' +
        'one case behind it, not a pattern — worth checking that it still gets things right.',
    },
    status: {
      ACTIVE: 'Active',
      INACTIVE: 'Inactive',
      DRAFT: 'Draft',
      DELETED: 'Removed',
    },
    staleAccountVersion: 'Account out of version',
    staleAccountVersionExplain:
      'This rule’s account is not in the published chart-of-accounts version. The classifier ' +
      'tries to resolve it through the crosswalk and drops the rule if it cannot — it may be ' +
      'deciding nothing.',
    matcher: {
      account: 'Account',
      vendor: 'Vendor',
      ncm: 'NCM',
      cfop: 'CFOP',
      keyword: 'Keyword',
      minUnitAmount: 'Minimum unit amount',
      none: 'No matchers',
      andJoiner: 'and',
      hint:
        'Every matcher you set must match at the same time. NCM matches by prefix ("8471" ' +
        'catches the whole computer range); CFOP matches exactly, on 4 digits.',
    },
    actions: {
      create: 'New rule',
      edit: 'Edit',
      activate: 'Activate',
      deactivate: 'Deactivate',
      save: 'Save',
      cancel: 'Cancel',
    },
    form: {
      name: 'Rule name',
      namePlaceholder: 'e.g. Laptops and desktops (NCM 8471)',
      priority: 'Priority',
      priorityHint: 'Lower is evaluated first. The first rule that matches wins.',
      classId: 'Target class',
      entityId: 'Entity',
      entityAll: 'All entities',
      minUnitAmountHint:
        'Compared against the UNIT amount, never the line total — otherwise fifty R$ 20 ' +
        'cables bought together would become one asset.',
      acknowledgeKeywordOnly: 'I confirm this keyword distinguishes these purchases',
      acknowledgeKeywordOnlyHint:
        'A keyword-only rule fires on any description containing the text. Prefer NCM, CFOP, ' +
        'account or vendor whenever you know which to use.',
      allowAutoConfirm: 'Allow automatic confirmation',
      allowAutoConfirmHint:
        'Lets the rule capitalise and post with no human review when nothing is ambiguous. ' +
        'Start it off and only turn it on after watching the rule get things right.',
    },
    toast: {
      created: 'Rule created.',
      updated: 'Rule updated.',
      activated: 'Rule activated.',
      deactivated: 'Rule deactivated.',
    },
  },

  policy: {
    title: 'Company policy',
    missingDefault: {
      title: 'No policy configured',
      description:
        'With no policy there is no threshold to judge against: no purchase is proposed as an ' +
        'asset, whatever it cost.',
    },
    scope: {
      label: 'Scope',
      tenantDefault: 'Tenant default',
      entity: 'Specific entity',
      hint: 'An entity policy takes precedence over the tenant default.',
    },
    fields: {
      capitalizationThresholdAmount: 'Capitalisation threshold',
      capitalizationThresholdAmountHint:
        'Applied PER UNIT, not to the line total. A class may set its own.',
      defaultIncrementalBorrowingRate: 'Incremental borrowing rate',
      defaultIncrementalBorrowingRateHint:
        'Per year. There is no official rate: it is the company’s own cost of borrowing and ' +
        'only it knows what that is. Overridable per contract.',
      lowValueLeaseThresholdAmount: 'Low-value lease threshold',
      lowValueLeaseThresholdAmountHint:
        'Per underlying asset. CPC 06 fixes no amount — this figure is company policy and has ' +
        'to be documented.',
      shortTermLeaseMaxMonths: 'Short-term maximum term',
      shortTermLeaseMaxMonthsHint:
        'Contracts at or below this term use the short-term exemption. CPC 06 (R2) §5(a) fixes ' +
        'the ceiling at 12 months — a longer term cannot be set.',
      shortTermLeaseMaxMonthsCeilingError:
        'CPC 06 (R2) §5(a) and Appendix A fix the short-term exemption at 12 months or less at ' +
        'commencement. That ceiling belongs to the standard, not the tenant, and cannot be ' +
        'exceeded — a shorter term (6 months, say) is fine.',
      capLeaseholdImprovementToTerm: 'Cap leasehold improvements at the lease term',
      capLeaseholdImprovementToTermHint:
        'Amortises the improvement over the remaining lease term rather than economic life.',
    },
    actions: { save: 'Save policy', edit: 'Edit' },
    toast: { saved: 'Policy saved.' },
    createOverride: {
      description:
        'No policy specific to {{entity}} yet. Until one exists, this entity’s purchases ' +
        'follow the tenant default shown above.',
      action: 'Create a policy for this entity',
    },
    createDefault: {
      action: 'Create the tenant default',
    },
    edit: {
      title: 'Edit policy',
      titleNewOverride: 'Create a policy for {{entity}}',
      titleNewDefault: 'Create the tenant default',
      newOverrideHint:
        'The values below start identical to the tenant default. Adjust whatever differs for ' +
        'this entity and save to create its own row — the tenant default is left untouched.',
      newDefaultHint:
        'No tenant default exists yet. Without one, no purchase is judged against a threshold ' +
        'and nothing is proposed as an asset until this form is saved.',
      cancel: 'Cancel',
      toast: { saved: 'Policy saved.' },
    },
  },

  bookRules: {
    edit: {
      action: 'Edit',
      title: 'Edit schedule',
      frameworkLocked: 'The framework comes from the book and is not editable.',
      // The fiscal lock (ALC-2978 048). Shown above the fields instead of the framework line
      // above so a reader does not need to infer "TAX_BASIS" implies "read-only" from the chip.
      fiscalLocked:
        'This book measures under IN RFB 1.700/2017 Annex III. Useful life, method and ' +
        "residual are not the entity's estimate to make and cannot be changed here — only the " +
        'norm reference is shown, for review.',
      fiscalLifeHint: 'Set by IN RFB 1.700/2017 Annex III, not editable on this screen.',
      lifeHint:
        "In months. It is the entity's estimate; CPC 27 §51 requires at least an annual review.",
      lifeIgnoredWhenNone:
        'With "Does not depreciate" the number is unused, but the field is required. Leave it at 1.',
      residualHint: 'How much of the cost remains at the end of useful life. Must be below 100%.',
      rateHint: 'Informational. Useful life in months is what the engine depreciates by.',
      normPlaceholder: 'CPC 27, engineering report 03/2026, IN RFB 1.700/2017 Annex III',
      normHint: 'Required: it is what supports the estimate at audit.',
      cancel: 'Cancel',
      save: 'Save schedule',
      toast: { saved: 'Schedule saved. It applies to the next assets capitalised.' },
    },
    title: 'Useful life per book',
    notRetroactive:
      'Changes here apply to the next assets capitalised. Assets already in service froze ' +
      'their schedule at capitalisation and keep the previous one.',
    empty: {
      title: 'No schedules configured',
      description:
        'A class with no rule in any book cannot capitalise: registering an asset is refused.',
    },
    columns: {
      class: 'Class',
      book: 'Book',
      framework: 'Framework',
      method: 'Method',
      usefulLife: 'Useful life',
      residual: 'Residual',
      referenceRate: 'Reference rate',
      norm: 'Norm',
    },
    method: {
      STRAIGHT_LINE: 'Straight line',
      DECLINING_BALANCE: 'Declining balance',
      UNITS_OF_PRODUCTION: 'Units of production',
      NONE: 'Does not depreciate',
    },
    months_one: '{{count}} month',
    months_other: '{{count}} months',
    fields: {
      usefulLifeMonths: 'Useful life (months)',
      residualRate: 'Residual value (%)',
      referenceAnnualRate: 'Reference annual rate (%)',
      referenceAnnualRateHint:
        'Informational: the engine uses useful life in months. Kept so reports cite the rate ' +
        'the accountant expects to see.',
      normReference: 'Norm reference',
      normReferencePlaceholder: 'e.g. IN RFB 1.700/2017 Anexo III',
    },
    actions: { edit: 'Edit schedule', save: 'Save' },
    toast: { saved: 'Schedule saved.' },
  },

  classes: {
    empty: {
      title: 'No classes visible',
      description:
        'No class matches the framework of this entity’s books. Contact Alchemy if you expected ' +
        'classes here.',
    },
    title: 'Asset classes',
    columns: {
      code: 'Code',
      name: 'Name',
      group: 'Group',
      assetKind: 'Fiscal nature',
      threshold: 'Own threshold',
      unitization: 'Unitisation',
      bookRules: 'Schedules',
      assets: 'Assets',
      status: 'Status',
    },
    locked: 'Set by Alchemy',
    lockedHint:
      'Code, group, fiscal nature and right-of-use are not editable: they resolve the ' +
      'depreciation the tax rules admit and the balance-sheet presentation. To change an ' +
      'asset’s nature, move it to another class.',
    rouRegraHint:
      'Right-of-use classes deliberately carry no schedule here: CPC 06 (R2) §32 amortises over ' +
      'the shorter of the lease term and useful life, and the term belongs to the contract, not ' +
      'the class. Lease recognition computes that schedule on its own.',
    noBookRules: 'No schedule — nothing capitalises into this class',
    usefulLife: {
      action: 'Set useful life',
      description:
        'CPC 27 §51 puts useful life on the entity’s own estimate. The fiscal schedule beside ' +
        'it comes from IN RFB 1.700/2017 Anexo III and is read-only — only the statutory book ' +
        'is written here.',
      unavailable:
        'This class has no statutory/fiscal pair to compare. Use the “Useful life by book” tab, ' +
        'which names the book on every row.',
    },
    bookRuleFromContract: 'Schedule set by the contract',
    bookRuleFromContractExplain:
      'A right-of-use asset amortises over the shorter of the lease term and the underlying ' +
      'asset’s useful life (CPC 06 (R2) §32). The term comes from each contract, not this ' +
      'class — lease recognition already computes the schedule on its own, even with no row ' +
      'here. This is not missing configuration.',
    noBookRulesLeaseholdExplain:
      'A leasehold improvement also amortises over the contract term, but the engine that would ' +
      'compute that schedule at capitalisation does not exist yet. Capitalising into this class ' +
      'today is refused with a clear message, not silently — this is not a forgotten setting.',
    thresholdInherited: 'Inherits the policy threshold',
    group: {
      PROPERTY_PLANT_EQUIPMENT: 'Property, plant and equipment',
      RIGHT_OF_USE: 'Right of use',
      INTANGIBLE: 'Intangible',
    },
    unitization: {
      PER_UNIT: 'One asset per unit',
      AGGREGATE: 'One asset per line',
      ASK: 'Ask',
    },
    rightOfUse: 'Right of use',
    leaseholdImprovement: 'Leasehold improvement',
    fields: {
      name: 'Name',
      description: 'Note',
      capitalizationThresholdAmount: 'Class threshold',
      capitalizationThresholdAmountHint: 'Empty inherits the company policy threshold.',
      unitizationPolicy: 'Unitisation',
      isLeaseholdImprovement: 'Is a leasehold improvement',
    },
    create: {
      action: 'New class',
      title: 'Create asset class',
      description:
        'Define the entity presentation class. Its statutory schedule is decided later; the ' +
        'fiscal schedule remains protected by law.',
      headerDescription: 'Add the classes that represent the operation and its disclosures.',
      code: 'Code',
      codePlaceholder: 'MEDICAL_EQUIPMENT',
      framework: 'Accounting framework',
      select: 'Select',
      fiscalLock:
        'Fiscal nature is not chosen here. A new class has no fiscal default until Alchemy ' +
        'provides a classification backed by legislation.',
      cancel: 'Cancel',
      submit: 'Create class',
      saving: 'Creating...',
      discardTitle: 'Discard the new class?',
      discardDescription: 'The information entered will be lost if you close this form.',
      discard: 'Discard',
      keepEditing: 'Keep editing',
      frameworkLoadError:
        'Accounting frameworks could not be loaded. Try again before creating the class.',
      retryFrameworks: 'Try again',
    },
    actions: { edit: 'Edit', save: 'Save' },
    toast: { saved: 'Class saved.', created: 'Class created.' },
  },

  onboarding: {
    repair: {
      title: 'Incomplete tax defaults',
      description: 'The tax book exists, but required default asset classes or rules are missing.',
      action: 'Restore tax defaults',
      confirmTitle: 'Restore tax defaults?',
      confirmDescription:
        'Only missing defaults for this entity will be created. Existing decisions and configured rules will be preserved.',
      confirm: 'Restore defaults',
      success: 'Tax defaults restored. Review the classes and rules before continuing.',
      error: 'Could not restore the tax defaults.',
    },
    loading: 'Loading asset module configuration…',
    unsaved: {
      title: 'Discard the unsaved estimate?',
      description:
        'This class useful-life choice will be lost. Save the estimate before continuing or stay on this step.',
      discard: 'Discard and continue',
      stay: 'Stay on this step',
    },
    entity: 'Entity',
    selectEntity: 'Select entity',
    steps: {
      books: 'Books',
      accounts: 'Account mapping',
      classRules: 'Class schedules',
      policy: 'Company policy',
    },
    actions: {
      leave: 'Go to assets',
      previous: 'Back',
      continue: 'Continue: {{step}}',
      finish: 'Finish and go to assets',
    },
    loadError: { title: 'Could not check module setup' },
    books: {
      title: 'Prepare both measurement schedules',
      description:
        'The statutory book records CPC accounting; the fiscal book measures in parallel without posting to the ledger.',
      statutory: 'Statutory schedule',
      fiscal: 'Fiscal schedule',
      statutoryMissing: 'Not configured',
      fiscalMissing: 'Pending',
      createStatutoryFirst:
        'This entity has no active statutory book. Create it in accounting settings before ' +
        'adding the fiscal schedule.',
      createStatutory: 'Create statutory book',
      createTitle: 'Create this entity fiscal schedule',
      code: 'Book code',
      name: 'Book name',
      memoExplanation:
        'The guide creates the book with TAX_BASIS framework and MEMO mode, plus its measurement ' +
        'scope. It never posts journals; useful lives come from IN RFB 1.700/2017 Annex III.',
      create: 'Create fiscal book',
      creating: 'Creating...',
      created: 'Fiscal book and law-backed schedules created.',
    },
    accounts: {
      title: 'Map all nine required accounts',
      description:
        'Without these nine FA slots, capitalisation, depreciation, disposal and leases cannot post.',
      complete: 'All required slots are mapped.',
      pending: 'Required slots still lack an account in this entity active chart.',
      configure: 'Configure system accounts',
    },
    classRules: {
      title: 'Decide the statutory class schedules',
      description:
        'CPC 27 §51 requires the entity to estimate useful life. An unanswered value stays ' +
        'aligned with tax, but it does not count as an entity decision.',
      fiscalLockedTitle: 'The fiscal schedule is read-only',
      fiscalLocked:
        'Useful life, method and residual in the TAX_BASIS book come from IN RFB 1.700/2017 ' +
        'Annex III. Neither this guide nor the rules page can change them.',
      unavailable:
        'This class has no statutory schedule yet — set one on the "Useful life per book" tab ' +
        'before answering here.',
      question:
        'Does the company depreciate {{className}} by the real replacement cycle, or by the ' +
        'fiscal rate?',
      realCycle: 'Real replacement cycle',
      realCycleDescription:
        'Enter the estimate approved by the entity. Alchemy suggests no useful life.',
      fiscalRate: 'Fiscal rate',
      fiscalRateDescription:
        'The entity decides that its economic estimate matches the fiscal useful life shown beside it.',
      months: 'Statutory useful life in months',
      noDefault: 'No default value',
      statutorySide: 'Statutory — entity estimate',
      fiscalSide: 'Fiscal — read-only',
      noFiscalRule:
        'No fiscal schedule yet. This class has no fiscal nature, and the active fiscal book ' +
        'has no schedule for it — saving the statutory decision creates a mirrored copy here.',
      referenceAmount: 'Reference asset amount used to calculate the temporary difference',
      additionTitle: 'This decision creates a temporary addition',
      additionDescription:
        'Excess statutory depreciation will be added in Parte A of e-Lalur and reversed over ' +
        'the fiscal life. Enter an amount above to see the monthly and first-year difference.',
      exclusionTitle: 'This decision creates a temporary exclusion',
      exclusionDescription:
        'Excess tax depreciation will be excluded in Parte A of e-Lalur and reversed over ' +
        'the statutory life. Enter an amount above to see the monthly and first-year difference.',
      answered: 'Answered',
      pending: 'Answer pending',
      save: 'Save statutory decision',
      saving: 'Saving...',
      saved: 'Statutory schedule updated. The fiscal schedule remained untouched.',
      savedWithMirror:
        'Statutory schedule updated. A mirrored fiscal schedule was created — confirm the ' +
        'fiscal useful life with the accountant.',
      saveError: 'The statutory decision could not be saved. Review the useful life and try again.',
    },
    policy: {
      title: 'Review company policy',
      description:
        'Confirm the capitalisation floor, low-value threshold, short-term ceiling and incremental rate.',
      configured: 'Policy configured',
      pending: 'Policy pending',
      review: 'Review policy',
    },
  },

  warnings: {
    title: 'Heads up',
    ASSET_CONFIG_LAST_ACTIVE_RULE_DEACTIVATED:
      'That was the last active rule. While none is active, no bill raises a candidate: the ' +
      'review queue stops receiving items and purchases are booked as expenses, silently.',
    ASSET_CONFIG_KEYWORD_ONLY_RULE:
      'Keyword-only rule. It fires on any description containing the text — replace it with ' +
      'NCM or CFOP as soon as you know which ones appear on these invoices.',
    ASSET_CONFIG_AUTO_CONFIRM_ENABLED:
      'Automatic confirmation is on: lines this rule matches will be capitalised and posted ' +
      'with no human review.',
    ASSET_CONFIG_BOOK_RULE_NOT_RETROACTIVE:
      'The new schedule applies only to future assets. {{assetsAlreadyInService}} already in ' +
      'service keep the schedule frozen at capitalisation — revising an in-service asset’s ' +
      'useful life is a change in estimate (CPC 23) applied prospectively.',
    ASSET_CONFIG_THRESHOLD_NOT_RETROACTIVE:
      'The new threshold applies to bills classified from now on. Candidates already raised ' +
      'keep the threshold recorded when they were proposed.',
    ASSET_CONFIG_RULE_ACCOUNT_SUPERSEDED_COA:
      'This rule’s account sits in a superseded chart-of-accounts version. The rule only ' +
      'matches if an unambiguous crosswalk reaches the version in force — without one it is ' +
      'dropped silently.',
    ASSET_CONFIG_FISCAL_RULE_MIRRORED:
      'This class’s fiscal schedule was created as a mirror of the statutory schedule you just ' +
      'saved — this class has no fiscal nature, and IN RFB 1.700/2017 Annex III has no line ' +
      'for it. Confirm the fiscal useful life with the accountant and adjust it on the ' +
      '"Useful life per book" tab if it needs to differ from the statutory one.',
  },

  errors: {
    ASSET_CONFIG_FIELD_IMMUTABLE:
      'This field is set by Alchemy and cannot be changed: it resolves fiscal classification ' +
      'and balance-sheet presentation. To change an asset’s nature, move it to another class.',
    ASSET_CONFIG_RULE_NO_MATCHER:
      'A rule with no matchers matches every line on every bill. Set at least an account, ' +
      'vendor, NCM, CFOP or keyword.',
    ASSET_CONFIG_RULE_AMOUNT_ONLY_MATCHER:
      'A minimum amount alone does not identify what was bought: the rule would capture fees, ' +
      'rent and tax payments above that amount. Combine it with another matcher.',
    ASSET_CONFIG_RULE_KEYWORD_ONLY_UNACKNOWLEDGED:
      'This rule matches on a keyword only, the least reliable matcher. Tick the confirmation ' +
      'in the form if the keyword genuinely distinguishes these purchases.',
    ASSET_CONFIG_RULE_KEYWORD_ONLY_AUTO_CONFIRM:
      'A keyword-only rule may never confirm automatically.',
    ASSET_CONFIG_RULE_CFOP_INVALID: 'A CFOP is exactly 4 digits.',
    ASSET_CONFIG_RULE_NCM_INVALID: 'An NCM prefix accepts digits only.',
    ASSET_CONFIG_CLASS_NOT_FOUND: 'Class not found, or not active.',
    ASSET_CONFIG_CLASS_CODE_DUPLICATE: 'A class already uses this code. Choose another identifier.',
    ASSET_CONFIG_CLASS_CODE_INVALID:
      'The code must start with a letter and use uppercase letters, numbers and _ only.',
    ASSET_CONFIG_CLASS_GROUP_ROU_MISMATCH:
      'Group and right-of-use status cannot disagree. Select the Right of use group.',
    ASSET_CONFIG_CLASS_FISCAL_FRAMEWORK_LOCKED:
      'Create classes in a statutory framework. TAX_BASIS follows tax law and does not accept user-defined classes.',
    ASSET_CONFIG_FRAMEWORK_NOT_FOUND:
      'The selected accounting framework is missing or inactive. Choose an available framework.',
    ASSET_CONFIG_BOOK_CODE_DUPLICATE:
      'A book already uses this code. Enter another code for the fiscal book.',
    ASSET_ONBOARDING_STATUTORY_BOOK_REQUIRED:
      'Create and link this entity BR_CPC statutory book before adding the fiscal schedule. ' +
      'The fiscal book inherits its calendar, currency and fiscal rules from it, so an ' +
      'IFRS book cannot be the base.',
    ASSET_ONBOARDING_FISCAL_BOOK_EXISTS:
      'This entity already has an active fiscal book. Refresh the onboarding status.',
    ASSET_ONBOARDING_FISCAL_BOOK_NOT_MEMO:
      'The fiscal book could not be safely created as MEMO. No changes were saved.',
    ASSET_CONFIG_RULE_ROU_CLASS_NOT_TARGETABLE:
      'Right-of-use classes take no capitalisation rule: a right-of-use asset comes from ' +
      'recognising a contract, never from a bill line.',
    ASSET_CONFIG_RULE_ACCOUNT_NOT_MATCHABLE:
      'This rule’s account belongs to a draft chart-of-accounts version: no bill was ever ' +
      'classified against it, so the rule would never match. Point it at the account in the ' +
      'published version.',
    ASSET_CONFIG_RULE_NOT_FOUND: 'Rule not found.',
    ASSET_CONFIG_BOOK_NOT_FOUND: 'Book not found.',
    ASSET_CONFIG_BOOK_RULE_NOT_FOUND: 'Schedule not found.',
    ASSET_CONFIG_BOOK_RULE_FISCAL_LOCKED:
      "This book's useful life, method and residual follow IN RFB 1.700/2017 Annex III and are " +
      "not the entity's estimate to make — they cannot be changed here.",
    ASSET_CONFIG_METHOD_NOT_IMPLEMENTED:
      'This method is not computed by the depreciation engine yet. Use Straight line ' +
      '(STRAIGHT_LINE) or Does not depreciate (NONE) — the only two that generate instalments today.',
    ASSET_CONFIG_POLICY_NOT_FOUND: 'Policy not found.',
    ASSET_CONFIG_ENTITY_NOT_FOUND: 'Entity not found.',
    ASSET_CONFIG_USEFUL_LIFE_INVALID: 'Useful life must be a whole number of months.',
    ASSET_CONFIG_RESIDUAL_RATE_INVALID:
      'Residual value is a fraction of cost and must be below 100%.',
    ASSET_CONFIG_RATE_OUT_OF_RANGE: 'Rate outside the accepted range.',
    ASSET_CONFIG_AMOUNT_INVALID: 'The amount cannot be negative.',
    ASSET_CONFIG_SHORT_TERM_MONTHS_INVALID:
      'The short-term bound must be a positive number of months.',
    ASSET_CONFIG_DELETE_NOT_SUPPORTED:
      'Records on this screen are deactivated, never deleted: history depends on them.',
  },
};
